import { useEffect, useRef } from 'react';
import type { CosmeticItem } from '../../api/types';
import { assetUrl } from './cosmeticsMap';
import { LottieLayer } from './LottieLayer';
import { onDeviceTilt, tgPlatform } from '../../telegram';

/** Единственный фон с параллаксом (решение владельца 02.10). */
const PARALLAX_CODES = new Set(['bg_gold']);
/** Телефон в Telegram: только мобильные клиенты, без ПК/веба. */
const TG_MOBILE = tgPlatform === 'ios' || tgPlatform === 'android';

/** Параллакс «Золотых частиц»: наклон телефона (DeviceOrientation) только
 *  в мобильном Telegram. Сдвиг ±10px через rAF; reduced motion — выключен. */
function useParallax(enabled: boolean) {
  const ref = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    if (!enabled || !TG_MOBILE) return;
    if (typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches)
      return;
    const el = ref.current;
    if (!el) return;
    el.style.transform = 'scale(1.06)'; // запас по краям под сдвиг
    let raf = 0;
    let tx = 0;
    let ty = 0;
    const apply = () => {
      raf = 0;
      el.style.transform = `translate(${tx.toFixed(1)}px, ${ty.toFixed(1)}px) scale(1.06)`;
    };
    const set = (nx: number, ny: number) => {
      // nx/ny ∈ [-1, 1] → сдвиг до 10px
      tx = Math.max(-1, Math.min(1, nx)) * 10;
      ty = Math.max(-1, Math.min(1, ny)) * 10;
      if (!raf) raf = requestAnimationFrame(apply);
    };
    // Наклон — через Telegram DeviceOrientation API (в webview Telegram
    // стандартные события не приходят), см. onDeviceTilt.
    const off = onDeviceTilt((beta, gamma) => set(gamma / 30, (beta - 40) / 30));
    return () => {
      off();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [enabled]);
  return ref;
}

/**
 * Фон профиля (слой под шапкой): webp — картинка cover, lottie — canvas.
 * Сверху и снизу растворение в фон приложения (.pbg-fade в theme.css).
 * Играет только пока профиль открыт (компонент размонтируется с экраном).
 * `parallax` — лёгкий сдвиг слоя от наклона телефона/курсора (6.8b).
 */
export function ProfileBg({
  cosmetic,
  parallax = false,
}: {
  cosmetic: CosmeticItem | null | undefined;
  parallax?: boolean;
}) {
  // Параллакс только у «Золотых частиц» — остальные фоны статичны
  const layerRef = useParallax(parallax && PARALLAX_CODES.has(cosmetic?.code ?? ''));
  if (!cosmetic) return null;
  const url = assetUrl(cosmetic.asset);
  if (!url) return null;
  return (
    <span className="pbg" aria-hidden>
      <span className="pbg-move" ref={layerRef}>
        {cosmetic.kind === 'webp' ? (
          <img src={url} alt="" />
        ) : (
          // Анимированные фоны всегда живут циклом (не режим по редкости)
          <LottieLayer src={url} mode="loop" className="pbg-lottie" width={360} height={240} />
        )}
      </span>
      <i className="pbg-fade" />
    </span>
  );
}
